import { useEffect, useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { LogOut, Settings, Briefcase, Image as ImageIcon, Mail, Shield, Trash2, Plus, Save, Edit2, Eye, EyeOff, AlertCircle, CheckCircle2, ExternalLink } from "lucide-react";
import { toast, Toaster } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { api, formatApiError } from "@/lib/api";
import { useAuth, useSettings } from "@/context/AppContext";
import { compressImage, compressMany, readVideosMany, LIMITS } from "@/lib/imageUtils";
import { describeImage, imageSizeKB } from "@/lib/imageMeta";
import useConfirm from "@/hooks/useConfirm";
const handleImage = async (file, setter) => {
  try {
    const data = await compressImage(file);
    setter(data);
    const kb = imageSizeKB(data);
    toast.success(`Image optimisée — ${kb} Ko`);
    return data;
  } catch (e) {
    toast.error(e.message);
    return null;
  }
};

// Compare deux objets pour détecter modifications non enregistrées
const isDirty = (a, b) => JSON.stringify(a) !== JSON.stringify(b);

// Bandeau d'état "non enregistré" / "enregistré"
function SaveStatusBadge({ dirty }) {
  if (dirty) {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs text-corten bg-corten/10 border border-corten/30 px-2.5 py-1 rounded-full">
        <AlertCircle className="w-3 h-3" /> Modifications non enregistrées
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 text-xs text-forest bg-forest/10 border border-forest/30 px-2.5 py-1 rounded-full">
      <CheckCircle2 className="w-3 h-3" /> Synchronisé avec la base
    </span>
  );
}

// Visualiseur d'image stockée (ouvre le base64/URL plein écran)
function StoredImagePreviewButton({ src, label = "Voir l'image stockée" }) {
  const [open, setOpen] = useState(false);
  if (!src) return null;
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-1.5 text-xs text-forest hover:text-corten link-underline"
        data-testid="preview-stored-image"
      >
        <ExternalLink className="w-3 h-3" /> {label}
      </button>
      {open && (
        <div onClick={() => setOpen(false)} className="fixed inset-0 z-[80] bg-forest/95 flex items-center justify-center p-6 cursor-zoom-out">
          <img src={src} alt="" className="max-w-full max-h-full object-contain" />
        </div>
      )}
    </>
  );
}

const REAL_CATS = [
  { id: "jardins-prives", label: "Jardins privés" },
  { id: "entretien", label: "Entretien" },
  { id: "plantations", label: "Plantations" },
  { id: "amenagements", label: "Aménagements" },
];

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const nav = useNavigate();

  const handleLogout = async () => {
    await logout();
    nav("/admin/login");
  };

  return (
    <div className="min-h-screen bg-bone">
      <Toaster richColors position="top-center" />
      <header className="bg-cream border-b border-sand sticky top-0 z-30">
        <div className="container-px max-w-7xl mx-auto h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="font-display text-xl text-forest">Pizzelli · Admin</span>
            <Badge variant="outline" className="border-forest/30 text-forest-soft">{user?.email}</Badge>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/" className="text-sm text-forest-soft hover:text-forest" data-testid="admin-view-site">Voir le site</Link>
            <Button variant="ghost" onClick={handleLogout} data-testid="admin-logout">
              <LogOut className="w-4 h-4 mr-2" /> Déconnexion
            </Button>
          </div>
        </div>
      </header>

      <main className="container-px max-w-7xl mx-auto py-10">
        <div className="mb-6 bg-bone border border-sand text-forest-soft text-xs px-4 py-3 flex items-start gap-2" data-testid="admin-info-banner">
          <span className="font-medium text-forest">ⓘ</span>
          <span>
            Les images sont automatiquement optimisées (max 1600&nbsp;px de large, qualité 80%). Limite par image&nbsp;: <strong>{LIMITS.MAX_IMAGE_RAW_MB}&nbsp;Mo</strong> · par vidéo&nbsp;: <strong>{LIMITS.MAX_VIDEO_MB}&nbsp;Mo</strong>.
          </span>
        </div>
        <Tabs defaultValue="settings" className="w-full">
          <TabsList className="bg-cream border border-sand h-auto p-1 flex-wrap">
            <TabsTrigger value="settings" data-testid="tab-settings"><Settings className="w-3.5 h-3.5 mr-2" />Site</TabsTrigger>
            <TabsTrigger value="services" data-testid="tab-services"><Briefcase className="w-3.5 h-3.5 mr-2" />Services</TabsTrigger>
            <TabsTrigger value="realisations" data-testid="tab-realisations"><ImageIcon className="w-3.5 h-3.5 mr-2" />Réalisations</TabsTrigger>
            <TabsTrigger value="messages" data-testid="tab-messages"><Mail className="w-3.5 h-3.5 mr-2" />Messages</TabsTrigger>
            <TabsTrigger value="account" data-testid="tab-account"><Shield className="w-3.5 h-3.5 mr-2" />Compte</TabsTrigger>
          </TabsList>

          <TabsContent value="settings" className="mt-6"><SettingsPanel /></TabsContent>
          <TabsContent value="services" className="mt-6"><ServicesPanel /></TabsContent>
          <TabsContent value="realisations" className="mt-6"><RealisationsPanel /></TabsContent>
          <TabsContent value="messages" className="mt-6"><MessagesPanel /></TabsContent>
          <TabsContent value="account" className="mt-6"><AccountPanel /></TabsContent>
        </Tabs>
      </main>
    </div>
  );
}

// ---------------- SETTINGS ----------------
function SettingsPanel() {
  const { settings, refresh } = useSettings();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (settings) setForm({ ...settings }); }, [settings]);
  if (!form) return null;

  const u = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const ut = (k, v) => setForm((f) => ({ ...f, targets: { ...f.targets, [k]: v } }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/site/settings", form);
      toast.success("Réglages enregistrés");
      refresh();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const onHero = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    await handleImage(f, (v) => u("hero_image", v));
    e.target.value = "";
  };
  const onAbout = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    await handleImage(f, (v) => u("about_image", v));
    e.target.value = "";
  };

  return (
    <div className="space-y-6">
      <Card title="Hero — page d'accueil">
        <Field label="Titre principal" value={form.hero_title} onChange={(v) => u("hero_title", v)} testid="set-hero-title" />
        <Field label="Sous-titre" value={form.hero_subtitle} onChange={(v) => u("hero_subtitle", v)} testid="set-hero-subtitle" textarea />
        <Field label="Libellé bouton CTA" value={form.cta_label} onChange={(v) => u("cta_label", v)} testid="set-cta" />
        <ImageField label="Image hero" value={form.hero_image} onUpload={onHero} onUrl={(v) => u("hero_image", v)} />
      </Card>

      <Card title="À propos">
        <Field label="Titre" value={form.about_title} onChange={(v) => u("about_title", v)} />
        <Field label="Texte" value={form.about_text} onChange={(v) => u("about_text", v)} textarea />
        <Field
          label="Valeurs (séparées par virgules)"
          value={(form.about_values || []).join(", ")}
          onChange={(v) => u("about_values", v.split(",").map((s) => s.trim()).filter(Boolean))}
        />
        <ImageField label="Image À propos" value={form.about_image} onUpload={onAbout} onUrl={(v) => u("about_image", v)} />
      </Card>

      <Card title="Pour qui ?">
        <Field label="Titre — Particuliers" value={form.targets.particuliers_title} onChange={(v) => ut("particuliers_title", v)} />
        <Field label="Texte — Particuliers" value={form.targets.particuliers_text} onChange={(v) => ut("particuliers_text", v)} textarea />
        <Field label="Titre — Professionnels" value={form.targets.professionnels_title} onChange={(v) => ut("professionnels_title", v)} />
        <Field label="Texte — Professionnels" value={form.targets.professionnels_text} onChange={(v) => ut("professionnels_text", v)} textarea />
      </Card>

      <Card title="Contact & coordonnées">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Téléphone" value={form.contact_phone} onChange={(v) => u("contact_phone", v)} />
          <Field label="WhatsApp (numéro intl)" value={form.contact_whatsapp} onChange={(v) => u("contact_whatsapp", v)} />
          <Field label="Email" value={form.contact_email} onChange={(v) => u("contact_email", v)} />
          <Field label="Adresse" value={form.contact_address} onChange={(v) => u("contact_address", v)} />
        </div>
        <Field label="Horaires" value={form.contact_hours} onChange={(v) => u("contact_hours", v)} textarea />

        <div className="pt-4 border-t border-sand space-y-3">
          <p className="text-[11px] uppercase tracking-[0.18em] text-forest/70 font-medium">Notifications automatiques</p>
          <p className="text-xs text-forest-soft -mt-1">
            À chaque demande de devis reçue depuis le site, un email est envoyé automatiquement à l'adresse ci-dessous avec toutes les informations du client. La demande reste enregistrée dans l'admin même si l'envoi échoue.
          </p>
          <Field
            label="Adresse email de réception (notification_email)"
            value={form.notification_email}
            onChange={(v) => u("notification_email", v)}
            testid="set-notification-email"
          />
          <label className="flex items-center justify-between gap-3 bg-bone border border-sand px-4 py-3 cursor-pointer select-none">
            <span className="text-sm text-forest">
              <strong>Envoi automatique d'email</strong>
              <span className="block text-xs text-forest-soft mt-0.5">
                {form.notifications_enabled ? "Activé — vous recevrez chaque demande par email." : "Désactivé — les demandes sont stockées sans envoi d'email."}
              </span>
            </span>
            <input
              type="checkbox"
              checked={!!form.notifications_enabled}
              onChange={(e) => u("notifications_enabled", e.target.checked)}
              className="w-5 h-5 accent-forest cursor-pointer"
              data-testid="toggle-notifications"
            />
          </label>
        </div>
      </Card>

      <Card title="Réseaux sociaux">
        <Field label="Lien Instagram" value={form.instagram_url} onChange={(v) => u("instagram_url", v)} />
        <Field label="Lien Facebook" value={form.facebook_url} onChange={(v) => u("facebook_url", v)} />
      </Card>

      <Card title="Partenaires">
        <p className="text-xs text-forest-soft -mt-2 mb-2">
          Ajoutez vos partenaires (fournisseurs, associations, certifications…). Logos affichés en pied de page avec lien cliquable.
        </p>
        <PartnersEditor value={form.partners || []} onChange={(v) => u("partners", v)} />
      </Card>

      <Card title="SEO">
        <Field label="Titre SEO" value={form.seo_title} onChange={(v) => u("seo_title", v)} />
        <Field label="Description SEO" value={form.seo_description} onChange={(v) => u("seo_description", v)} textarea />
        <Field label="Mots-clés" value={form.seo_keywords} onChange={(v) => u("seo_keywords", v)} />
      </Card>

      <Card title="Tailles de texte — Accueil & Navbar">
        <p className="text-xs text-forest-soft -mt-2 mb-2">
          Ajuste la taille (en pixels, valeur desktop) des principaux textes. Une mise à l'échelle responsive est appliquée automatiquement sur mobile. L'aperçu sous chaque réglage se met à jour en temps réel — pense à <strong>Enregistrer</strong> pour appliquer au site.
        </p>

        <div className="space-y-3">
          <p className="text-[11px] uppercase tracking-[0.18em] text-forest/70 font-medium">Page d'accueil</p>
          <SizeSlider
            label="Titre principal du Hero"
            min={40} max={140} fallback={80}
            value={form.hero_title_size}
            onChange={(v) => u("hero_title_size", v)}
            testid="size-hero-title"
            preview={(v) => (
              <h1
                className="font-display text-forest font-light tracking-tight leading-[0.95]"
                style={{ fontSize: `${v}px` }}
              >
                {form.hero_title || "Création et entretien d'espaces verts"}
              </h1>
            )}
          />
          <SizeSlider
            label="Sous-titre du Hero"
            min={14} max={28} fallback={18}
            value={form.hero_subtitle_size}
            onChange={(v) => u("hero_subtitle_size", v)}
            testid="size-hero-subtitle"
            preview={(v) => (
              <p
                className="text-forest-soft leading-relaxed font-light max-w-xl"
                style={{ fontSize: `${v}px` }}
              >
                {form.hero_subtitle || "Des jardins soignés, durables et adaptés à vos envies."}
              </p>
            )}
          />
          <SizeSlider
            label="Petit libellé au-dessus du titre Hero"
            min={10} max={16} fallback={11}
            value={form.hero_label_size}
            onChange={(v) => u("hero_label_size", v)}
            testid="size-hero-label"
            preview={(v) => (
              <p
                className="uppercase tracking-[0.22em] text-forest/80 font-medium"
                style={{ fontSize: `${v}px` }}
              >
                Pizzelli Paysagiste · Moudon, Vaud
              </p>
            )}
          />
          <SizeSlider
            label="Titres de sections (À propos, Services, Réalisations)"
            min={28} max={80} fallback={56}
            value={form.section_title_size}
            onChange={(v) => u("section_title_size", v)}
            testid="size-section-title"
            preview={(v) => (
              <h2
                className="font-display text-forest font-light tracking-tight leading-[1.05]"
                style={{ fontSize: `${v}px` }}
              >
                Le savoir-faire d'un paysagiste passionné
              </h2>
            )}
          />
          <SizeSlider
            label="Texte de section (paragraphes)"
            min={13} max={22} fallback={16}
            value={form.section_text_size}
            onChange={(v) => u("section_text_size", v)}
            testid="size-section-text"
            preview={(v) => (
              <p
                className="text-forest-soft leading-relaxed"
                style={{ fontSize: `${v}px` }}
              >
                Nous accompagnons particuliers et professionnels dans la création et l'entretien de leurs espaces extérieurs.
              </p>
            )}
          />
        </div>

        <div className="pt-4 border-t border-sand space-y-3">
          <p className="text-[11px] uppercase tracking-[0.18em] text-forest/70 font-medium">Barre de navigation</p>
          <SizeSlider
            label="Liens de navigation"
            min={11} max={18} fallback={13}
            value={form.navbar_link_size}
            onChange={(v) => u("navbar_link_size", v)}
            testid="size-navbar-link"
            preview={(v) => (
              <nav className="flex flex-wrap items-center gap-6">
                {["Accueil", "À propos", "Services", "Réalisations", "Contact"].map((l, i) => (
                  <span
                    key={i}
                    className={`tracking-wide ${i === 0 ? "text-forest" : "text-forest-soft"}`}
                    style={{ fontSize: `${v}px` }}
                  >
                    {l}
                  </span>
                ))}
              </nav>
            )}
          />
          <SizeSlider
            label="Bouton « Demander un devis »"
            min={11} max={18} fallback={13}
            value={form.navbar_btn_size}
            onChange={(v) => u("navbar_btn_size", v)}
            testid="size-navbar-btn"
            preview={(v) => (
              <span
                className="inline-flex items-center bg-forest text-cream px-6 py-3 tracking-wide font-medium"
                style={{ fontSize: `${v}px` }}
              >
                {form.cta_label || "Demander un devis"}
              </span>
            )}
          />
        </div>
      </Card>

      <Card title="Identité">
        <Field label="Nom de l'entreprise" value={form.company_name} onChange={(v) => u("company_name", v)} />
        <Field label="Tagline" value={form.company_tagline} onChange={(v) => u("company_tagline", v)} />

        <ImageField
          label="Logo principal — navbar (PNG transparent recommandé)"
          value={form.logo}
          onUpload={async (e) => {
            const f = e.target.files?.[0]; if (!f) return;
            await handleImage(f, (v) => u("logo", v));
            e.target.value = "";
          }}
          onUrl={(v) => u("logo", v)}
        />
        {form.logo && (
          <button
            type="button"
            onClick={() => u("logo", "")}
            className="text-corten text-xs link-underline"
            data-testid="logo-remove"
          >
            Supprimer le logo principal (revenir au défaut)
          </button>
        )}

        <div className="pt-4 border-t border-sand">
          <ImageField
            label="Logo clair — footer foncé (version blanche/claire). Optionnel : laisse vide pour utiliser le logo principal."
            value={form.logo_light}
            onUpload={async (e) => {
              const f = e.target.files?.[0]; if (!f) return;
              await handleImage(f, (v) => u("logo_light", v));
              e.target.value = "";
            }}
            onUrl={(v) => u("logo_light", v)}
          />
          {form.logo_light && (
            <button
              type="button"
              onClick={() => u("logo_light", "")}
              className="text-corten text-xs link-underline"
              data-testid="logo-light-remove"
            >
              Supprimer le logo clair
            </button>
          )}
        </div>

        <div className="pt-4 border-t border-sand grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <Label className="label-eyebrow block mb-2">Taille logo navbar (px)</Label>
            <Input
              type="number"
              min="24"
              max="120"
              value={form.logo_size ?? 40}
              onChange={(e) => u("logo_size", parseInt(e.target.value || "40", 10))}
              data-testid="logo-size"
            />
            <p className="text-[10px] text-forest-soft mt-1">24 – 120 px (défaut 40)</p>
          </div>
          <div>
            <Label className="label-eyebrow block mb-2">Taille logo footer (px)</Label>
            <Input
              type="number"
              min="24"
              max="140"
              value={form.logo_light_size ?? 48}
              onChange={(e) => u("logo_light_size", parseInt(e.target.value || "48", 10))}
              data-testid="logo-light-size"
            />
            <p className="text-[10px] text-forest-soft mt-1">24 – 140 px (défaut 48)</p>
          </div>
          <div>
            <Label className="label-eyebrow block mb-2">Taille logos partenaires (px)</Label>
            <Input
              type="number"
              min="40"
              max="180"
              value={form.partners_logo_size ?? 80}
              onChange={(e) => u("partners_logo_size", parseInt(e.target.value || "80", 10))}
              data-testid="partners-logo-size"
            />
            <p className="text-[10px] text-forest-soft mt-1">40 – 180 px (défaut 80)</p>
          </div>
        </div>
      </Card>

      <div className="flex justify-end sticky bottom-4 z-10">
        <Button onClick={save} disabled={saving} className="bg-forest hover:bg-forest-deep text-cream" data-testid="save-settings">
          <Save className="w-4 h-4 mr-2" /> {saving ? "Enregistrement..." : "Enregistrer"}
        </Button>
      </div>
    </div>
  );
}

function Card({ title, children }) {
  return (
    <section className="bg-cream border border-sand p-6 md:p-8 space-y-4">
      <h3 className="font-display text-xl text-forest">{title}</h3>
      <div className="space-y-4">{children}</div>
    </section>
  );
}

function Field({ label, value, onChange, textarea, testid }) {
  return (
    <div>
      <Label className="label-eyebrow block mb-2">{label}</Label>
      {textarea ? (
        <Textarea data-testid={testid} value={value || ""} onChange={(e) => onChange(e.target.value)} rows={4} />
      ) : (
        <Input data-testid={testid} value={value || ""} onChange={(e) => onChange(e.target.value)} />
      )}
    </div>
  );
}

function SizeSlider({ label, value, onChange, min, max, fallback, testid, preview }) {
  const v = Number.isFinite(value) ? value : fallback;
  const setSafe = (raw) => {
    const n = parseInt(raw, 10);
    if (Number.isNaN(n)) return onChange(fallback);
    onChange(Math.max(min, Math.min(max, n)));
  };
  return (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <Label className="label-eyebrow !mb-0">{label}</Label>
        <span className="text-xs text-forest font-medium tabular-nums">{v} px</span>
      </div>
      <div className="flex items-center gap-3">
        <input
          type="range"
          min={min}
          max={max}
          step={1}
          value={v}
          onChange={(e) => setSafe(e.target.value)}
          className="flex-1 accent-forest cursor-pointer"
          data-testid={`${testid}-range`}
        />
        <Input
          type="number"
          min={min}
          max={max}
          value={v}
          onChange={(e) => setSafe(e.target.value)}
          className="w-20"
          data-testid={testid}
        />
      </div>
      <p className="text-[10px] text-forest-soft mt-1">{min} – {max} px (défaut {fallback})</p>
      {preview && (
        <div
          className="mt-2 bg-bone border border-sand px-4 py-3 overflow-hidden"
          data-testid={`${testid}-preview`}
        >
          <p className="text-[9px] uppercase tracking-[0.18em] text-forest/50 mb-2">Aperçu</p>
          {typeof preview === "function" ? preview(v) : preview}
        </div>
      )}
    </div>
  );
}

function ImageField({ label, value, onUpload, onUrl }) {
  return (
    <div>
      <Label className="label-eyebrow block mb-2">{label}</Label>
      {value && <img src={value} alt="" className="w-40 aspect-video object-cover mb-3 border border-sand" />}
      <div className="flex flex-col sm:flex-row gap-2">
        <Input placeholder="URL https://..." value={value?.startsWith("http") ? value : ""} onChange={(e) => onUrl(e.target.value)} />
        <label className="inline-flex items-center gap-2 px-4 py-2 border border-sand cursor-pointer hover:bg-bone text-sm">
          <Plus className="w-4 h-4" /> Téléverser
          <input type="file" accept="image/*" className="hidden" onChange={onUpload} />
        </label>
      </div>
    </div>
  );
}

function PartnersEditor({ value, onChange }) {
  const newId = () => `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  const update = (id, patch) =>
    onChange(value.map((p) => (p.id === id ? { ...p, ...patch } : p)));
  const remove = (id) => onChange(value.filter((p) => p.id !== id));
  const add = () =>
    onChange([...value, { id: newId(), name: "", url: "", logo: "" }]);
  const onLogo = async (id, e) => {
    const f = e.target.files?.[0]; if (!f) return;
    try {
      const data = await compressImage(f);
      update(id, { logo: data });
      toast.success("Logo optimisé");
    } catch (err) { toast.error(err.message); }
    e.target.value = "";
  };

  return (
    <div className="space-y-4">
      {value.length === 0 && (
        <p className="text-sm text-forest-soft italic">Aucun partenaire pour le moment.</p>
      )}
      {value.map((p, i) => (
        <div
          key={p.id}
          className="border border-sand p-4 grid grid-cols-1 md:grid-cols-12 gap-3 items-start bg-background"
          data-testid={`partner-row-${i}`}
        >
          <div className="md:col-span-2 flex items-center justify-center bg-bone aspect-[3/2] border border-sand">
            {p.logo ? (
              <img src={p.logo} alt={p.name} className="max-h-full max-w-full object-contain p-2" />
            ) : (
              <span className="text-forest-soft text-xs">Pas de logo</span>
            )}
          </div>
          <div className="md:col-span-9 grid grid-cols-1 sm:grid-cols-2 gap-2">
            <Input
              placeholder="Nom du partenaire"
              value={p.name}
              onChange={(e) => update(p.id, { name: e.target.value })}
              data-testid={`partner-name-${i}`}
            />
            <Input
              placeholder="https://..."
              value={p.url}
              onChange={(e) => update(p.id, { url: e.target.value })}
              data-testid={`partner-url-${i}`}
            />
            <Input
              placeholder="URL du logo (ou téléverser →)"
              value={p.logo?.startsWith("http") ? p.logo : ""}
              onChange={(e) => update(p.id, { logo: e.target.value })}
              className="sm:col-span-2"
            />
            <label className="inline-flex items-center gap-2 px-3 py-2 border border-sand cursor-pointer hover:bg-bone text-xs sm:col-span-2 justify-center">
              <Plus className="w-3.5 h-3.5" /> Téléverser un logo
              <input type="file" accept="image/*" className="hidden" onChange={(e) => onLogo(p.id, e)} />
            </label>
          </div>
          <div className="md:col-span-1 flex md:flex-col gap-2 justify-end">
            <Button
              size="sm"
              variant="ghost"
              onClick={() => remove(p.id)}
              className="text-corten"
              data-testid={`partner-remove-${i}`}
            >
              <Trash2 className="w-3.5 h-3.5" />
            </Button>
          </div>
        </div>
      ))}
      <Button onClick={add} variant="outline" data-testid="partner-add">
        <Plus className="w-4 h-4 mr-2" /> Ajouter un partenaire
      </Button>
    </div>
  );
}

// ---------------- SERVICES ----------------
function ServicesPanel() {
  const [items, setItems] = useState([]);
  const [edit, setEdit] = useState(null);
  const { confirm, dialog: confirmDialog } = useConfirm();

  const load = () => api.get("/services").then((r) => setItems(r.data || []));
  useEffect(() => { load(); }, []);

  // onSave doit retourner les données fraîchement vérifiées en base, ou throw
  const save = async (data) => {
    let saved;
    if (data.id) {
      const { data: r } = await api.put(`/services/${data.id}`, data);
      saved = r;
    } else {
      const { data: r } = await api.post("/services", data);
      saved = r;
    }
    // Re-vérification : on relit explicitement depuis l'API
    const { data: list } = await api.get("/services");
    const verified = list.find((s) => s.id === saved.id);
    if (!verified) throw new Error("La modification n'a pas pu être vérifiée en base.");
    setItems(list);
    return verified;
  };

  const del = async (s) => {
    const ok = await confirm({
      title: "Supprimer ce service ?",
      description: `Le service "${s.title}" sera supprimé définitivement.\nCette action est irréversible.`,
      confirmLabel: "Supprimer",
    });
    if (!ok) return;
    try {
      await api.delete(`/services/${s.id}`);
      toast.success("Service supprimé");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Échec de la suppression");
    }
  };

  return (
    <div>
      {confirmDialog}
      <div className="flex justify-between items-center mb-6">
        <h3 className="font-display text-2xl text-forest">Services</h3>
        <Button onClick={() => setEdit({ title: "", description: "", image: "", icon: "Leaf", order: items.length })} className="bg-forest hover:bg-forest-deep text-cream" data-testid="add-service">
          <Plus className="w-4 h-4 mr-2" /> Ajouter
        </Button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((s) => {
          const meta = describeImage(s.image);
          return (
            <div key={s.id} className="bg-cream border border-sand overflow-hidden">
              {s.image && <img src={s.image} alt="" className="w-full aspect-[4/3] object-cover" />}
              <div className="p-4">
                <h4 className="font-display text-lg text-forest">{s.title}</h4>
                <p className="text-xs text-forest-soft line-clamp-2 mt-1">{s.description}</p>
                <p className="text-[10px] text-forest-soft/70 mt-1">{meta.label}</p>
                <div className="flex gap-2 mt-3">
                  <Button size="sm" variant="outline" onClick={() => setEdit(s)}><Edit2 className="w-3 h-3" /></Button>
                  <Button size="sm" variant="ghost" onClick={() => del(s)} className="text-corten"><Trash2 className="w-3 h-3" /></Button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
      {edit && <ServiceDialog data={edit} onClose={() => setEdit(null)} onSave={save} />}
    </div>
  );
}

function ServiceDialog({ data, onClose, onSave }) {
  const [server, setServer] = useState(data);    // dernier état confirmé en base
  const [f, setF] = useState(data);                // brouillon
  const [saving, setSaving] = useState(false);
  const dirty = isDirty(f, server);
  const { confirm, dialog: confirmDialog } = useConfirm();

  const onImg = async (e) => {
    const fl = e.target.files?.[0]; if (!fl) return;
    const data = await handleImage(fl, () => {});
    if (data) setF((s) => ({ ...s, image: data }));
    e.target.value = "";
  };

  const removeImage = async () => {
    const ok = await confirm({
      title: "Retirer l'image ?",
      description: "L'image actuelle sera retirée de ce service au prochain enregistrement.",
      confirmLabel: "Retirer",
    });
    if (ok) setF((s) => ({ ...s, image: "" }));
  };

  const handleClose = async () => {
    if (!dirty) return onClose();
    const ok = await confirm({
      title: "Quitter sans enregistrer ?",
      description: "Vos modifications n'ont pas été enregistrées et seront perdues.",
      confirmLabel: "Quitter",
    });
    if (ok) onClose();
  };

  const submit = async () => {
    setSaving(true);
    try {
      const verified = await onSave(f);
      setServer(verified);
      setF(verified);
      const meta = describeImage(verified.image);
      toast.success(`✓ Service enregistré et vérifié en base · ${meta.label}`);
      // On garde le dialog ouvert pour que l'admin puisse vérifier l'aperçu
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message || "Échec de la sauvegarde");
      // dialog reste ouvert
    } finally {
      setSaving(false);
    }
  };

  const meta = describeImage(server.image);

  return (
    <Dialog open onOpenChange={(o) => { if (!o) handleClose(); }}>
      <DialogContent className="max-w-lg">
        {confirmDialog}
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between gap-3">
            <span>{server.id ? "Modifier" : "Ajouter"} un service</span>
            <SaveStatusBadge dirty={dirty} />
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <Input placeholder="Titre" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="service-title" />
          <Textarea placeholder="Description" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} rows={3} />
          <Select value={f.icon} onValueChange={(v) => setF({ ...f, icon: v })}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              {["Leaf","Sprout","Scissors","Grid3x3","TreePine","Mountain","Square","Sparkles"].map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}
            </SelectContent>
          </Select>

          <div className="space-y-2">
            <Label className="label-eyebrow">Image</Label>
            <Input
              placeholder="URL https://..."
              value={f.image?.startsWith("http") ? f.image : ""}
              onChange={(e) => setF({ ...f, image: e.target.value })}
            />
            <div className="flex gap-2 flex-wrap">
              <label className="inline-flex items-center gap-2 px-4 py-2 border border-sand cursor-pointer hover:bg-bone text-sm">
                <Plus className="w-4 h-4" /> Téléverser
                <input type="file" accept="image/*" className="hidden" onChange={onImg} />
              </label>
              {f.image && (
                <button type="button" onClick={removeImage} className="inline-flex items-center gap-1.5 px-3 py-2 text-corten text-xs hover:underline">
                  <Trash2 className="w-3.5 h-3.5" /> Retirer l'image
                </button>
              )}
            </div>
            {f.image && <img src={f.image} alt="" className="w-full aspect-video object-cover" />}
            <div className="flex items-center justify-between text-xs text-forest-soft">
              <span>Aperçu (brouillon)</span>
              {server.image && <StoredImagePreviewButton src={server.image} label={`Image en base : ${meta.label}`} />}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={saving}>Fermer</Button>
          <Button
            onClick={submit}
            disabled={saving || !dirty}
            className="bg-forest hover:bg-forest-deep text-cream"
            data-testid="service-save"
          >
            <Save className="w-4 h-4 mr-2" /> {saving ? "Enregistrement..." : dirty ? "Enregistrer" : "✓ Enregistré"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------- REALISATIONS ----------------
function RealisationsPanel() {
  const [items, setItems] = useState([]);
  const [edit, setEdit] = useState(null);
  const { confirm, dialog: confirmDialog } = useConfirm();
  const load = () => api.get("/realizations").then((r) => setItems(r.data || []));
  useEffect(() => { load(); }, []);

  const save = async (d) => {
    let saved;
    if (d.id) {
      const { data: r } = await api.put(`/realizations/${d.id}`, d);
      saved = r;
    } else {
      const { data: r } = await api.post("/realizations", d);
      saved = r;
    }
    // Re-vérification : on relit depuis l'API
    const { data: list } = await api.get("/realizations");
    const verified = list.find((x) => x.id === saved.id);
    if (!verified) throw new Error("Modification non vérifiée en base.");
    setItems(list);
    return verified;
  };

  const del = async (r) => {
    const ok = await confirm({
      title: "Supprimer cette réalisation ?",
      description: `"${r.title}" sera supprimée définitivement avec toutes ses images et légendes.\nCette action est irréversible.`,
      confirmLabel: "Supprimer",
    });
    if (!ok) return;
    try {
      await api.delete(`/realizations/${r.id}`);
      toast.success("Réalisation supprimée");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Échec de la suppression");
    }
  };

  return (
    <div>
      {confirmDialog}
      <div className="flex justify-between items-center mb-6">
        <h3 className="font-display text-2xl text-forest">Réalisations</h3>
        <Button onClick={() => setEdit({ title: "", description: "", category: "jardins-prives", cover: "", images: [], videos: [] })} className="bg-forest hover:bg-forest-deep text-cream" data-testid="add-realisation">
          <Plus className="w-4 h-4 mr-2" /> Ajouter
        </Button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((r) => {
          const meta = describeImage(r.cover);
          const customCount = (r.images || []).filter((i) => {
            const s = typeof i === "string" ? i : i.src;
            return s?.startsWith("data:");
          }).length;
          return (
            <div key={r.id} className="bg-cream border border-sand overflow-hidden">
              {r.cover && <img src={r.cover} alt="" className="w-full aspect-[4/3] object-cover" />}
              <div className="p-4">
                <Badge variant="outline" className="text-xs mb-2">{REAL_CATS.find((c) => c.id === r.category)?.label}</Badge>
                <h4 className="font-display text-lg text-forest">{r.title}</h4>
                <p className="text-[10px] text-forest-soft/70 mt-1">
                  Couverture : {meta.label} · Galerie : {(r.images || []).length} image(s){customCount > 0 ? ` (${customCount} perso)` : ""}
                </p>
                <div className="flex gap-2 mt-3">
                  <Button size="sm" variant="outline" onClick={() => setEdit(r)}><Edit2 className="w-3 h-3" /></Button>
                  <Button size="sm" variant="ghost" onClick={() => del(r)} className="text-corten"><Trash2 className="w-3 h-3" /></Button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
      {edit && <RealisationDialog data={edit} onClose={() => setEdit(null)} onSave={save} />}
    </div>
  );
}

function RealisationDialog({ data, onClose, onSave }) {
  const [server, setServer] = useState(data);
  const [f, setF] = useState(data);
  const [saving, setSaving] = useState(false);
  const dirty = isDirty(f, server);
  const { confirm, dialog: confirmDialog } = useConfirm();

  const onCover = async (e) => {
    const fl = e.target.files?.[0]; if (!fl) return;
    const data = await handleImage(fl, () => {});
    if (data) setF((s) => ({ ...s, cover: data }));
    e.target.value = "";
  };
  const onAddImages = async (e) => {
    const files = Array.from(e.target.files || []);
    const { results, errors } = await compressMany(files);
    if (results.length) {
      const newImgs = results.map((src) => ({ src, caption: "" }));
      setF((s) => ({ ...s, images: [...(s.images || []), ...newImgs] }));
      const totalKb = results.reduce((acc, src) => acc + (imageSizeKB(src) || 0), 0);
      toast.success(`${results.length} image(s) optimisée(s) — total ${totalKb} Ko (à enregistrer)`);
    }
    errors.forEach((er) => toast.error(`${er.name} : ${er.message}`));
    e.target.value = "";
  };
  const onAddVideos = async (e) => {
    const files = Array.from(e.target.files || []);
    const { results, errors } = await readVideosMany(files);
    if (results.length) {
      setF((s) => ({ ...s, videos: [...(s.videos || []), ...results] }));
      toast.success(`${results.length} vidéo(s) ajoutée(s) (à enregistrer)`);
    }
    errors.forEach((er) => toast.error(`${er.name} : ${er.message}`));
    e.target.value = "";
  };

  const removeGalleryImage = async (i) => {
    const ok = await confirm({
      title: "Supprimer cette image ?",
      description: `L'image n°${i + 1} et sa légende seront retirées de la galerie après enregistrement.`,
      confirmLabel: "Supprimer",
    });
    if (ok) setF({ ...f, images: f.images.filter((_, k) => k !== i) });
  };

  const removeVideo = async (i) => {
    const ok = await confirm({
      title: "Supprimer cette vidéo ?",
      description: `La vidéo n°${i + 1} sera retirée après enregistrement.`,
      confirmLabel: "Supprimer",
    });
    if (ok) setF({ ...f, videos: f.videos.filter((_, k) => k !== i) });
  };

  const removeCover = async () => {
    const ok = await confirm({
      title: "Retirer la couverture ?",
      description: "L'image de couverture sera retirée à l'enregistrement.",
      confirmLabel: "Retirer",
    });
    if (ok) setF({ ...f, cover: "" });
  };

  const handleClose = async () => {
    if (!dirty) return onClose();
    const ok = await confirm({
      title: "Quitter sans enregistrer ?",
      description: "Vos modifications n'ont pas été enregistrées et seront perdues.",
      confirmLabel: "Quitter",
    });
    if (ok) onClose();
  };

  const submit = async () => {
    setSaving(true);
    try {
      const verified = await onSave(f);
      setServer(verified);
      setF(verified);
      const meta = describeImage(verified.cover);
      const galleryCount = (verified.images || []).length;
      toast.success(`✓ Enregistrée et vérifiée en base · Couverture: ${meta.label} · ${galleryCount} image(s) galerie`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message || "Échec de la sauvegarde — la fenêtre reste ouverte, vos modifications sont préservées");
    } finally {
      setSaving(false);
    }
  };

  const coverMeta = describeImage(server.cover);

  return (
    <Dialog open onOpenChange={(o) => { if (!o) handleClose(); }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        {confirmDialog}
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between gap-3">
            <span>{server.id ? "Modifier" : "Ajouter"} une réalisation</span>
            <SaveStatusBadge dirty={dirty} />
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <Input placeholder="Titre" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="real-title" />
          <Textarea placeholder="Description" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} rows={3} />
          <Select value={f.category} onValueChange={(v) => setF({ ...f, category: v })}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              {REAL_CATS.map((c) => <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>)}
            </SelectContent>
          </Select>

          <div>
            <Label className="label-eyebrow block mb-2">Image de couverture</Label>
            {f.cover && <img src={f.cover} alt="" className="w-40 aspect-video object-cover mb-2 border border-sand" />}
            <div className="flex gap-2 flex-wrap">
              <Input placeholder="URL image" value={f.cover?.startsWith("http") ? f.cover : ""} onChange={(e) => setF({ ...f, cover: e.target.value })} />
              <label className="inline-flex items-center gap-2 px-4 py-2 border border-sand cursor-pointer hover:bg-bone text-sm whitespace-nowrap">
                <Plus className="w-4 h-4" /> Téléverser
                <input type="file" accept="image/*" className="hidden" onChange={onCover} />
              </label>
              {f.cover && (
                <button type="button" onClick={removeCover} className="inline-flex items-center gap-1.5 px-3 py-2 text-corten text-xs hover:underline">
                  <Trash2 className="w-3.5 h-3.5" /> Retirer
                </button>
              )}
            </div>
            <div className="flex items-center justify-between text-xs text-forest-soft mt-2">
              <span>Aperçu (brouillon)</span>
              {server.cover && <StoredImagePreviewButton src={server.cover} label={`Couverture en base : ${coverMeta.label}`} />}
            </div>
          </div>

          <div>
            <Label className="label-eyebrow block mb-2">Galerie & légendes ({(f.images || []).length})</Label>
            <p className="text-xs text-forest-soft mb-3">
              Cliquez dans la zone à droite de chaque image pour <strong>écrire la légende</strong> qui s'affichera sur le site (titre, anecdote, matériau, lieu...). Les modifications sont actives <strong>après enregistrement</strong>.
            </p>
            <div className="space-y-3 mb-3">
              {(f.images || []).map((img, i) => {
                const src = typeof img === "string" ? img : img.src;
                const caption = typeof img === "string" ? "" : (img.caption || "");
                const sz = imageSizeKB(src);
                const isB64 = src?.startsWith("data:");
                const isCloudinary = !isB64 && (src?.includes("cloudinary.com") || src?.includes("res.cloudinary"));
                const isExternalUrl = !isB64 && !!src && src.startsWith("http");
                const sourceLabel = isB64 ? `Base64 (${sz} Ko)` : isCloudinary ? "Cloudinary ⚡" : isExternalUrl ? "URL externe" : "—";
                const sourceColor = isB64 ? "text-forest-soft" : isCloudinary ? "text-forest font-medium" : "text-forest-soft";
                const updateField = (patch) => {
                  const next = [...(f.images || [])];
                  next[i] = { src, caption, ...patch };
                  setF({ ...f, images: next });
                };
                const onReplaceUpload = async (e) => {
                  const fl = e.target.files?.[0]; if (!fl) return;
                  try {
                    const dataUrl = await compressImage(fl);
                    const kb = imageSizeKB(dataUrl);
                    updateField({ src: dataUrl });
                    toast.success(`Image n°${i + 1} remplacée — ${kb} Ko (à enregistrer)`);
                  } catch (err) {
                    toast.error(err.message);
                  }
                  e.target.value = "";
                };
                return (
                  <div key={i} className="border border-sand p-3 bg-bone/40 rounded space-y-3">
                    <div className="flex gap-3 items-start">
                      <div className="shrink-0 w-28">
                        <img src={src} alt="" className="w-28 h-28 object-cover" />
                        <p className={`text-[10px] mt-1 text-center ${sourceColor}`}>{sourceLabel}</p>
                      </div>
                      <div className="flex-1 flex flex-col">
                        <span className="text-[10px] uppercase tracking-[0.2em] text-corten font-medium mb-1">
                          Légende image {i + 1}
                        </span>
                        <Textarea
                          placeholder="Ex: Mur en pierre originaire de Bourgogne, montage à sec sans ciment..."
                          value={caption}
                          rows={3}
                          className="flex-1 bg-cream"
                          onChange={(e) => updateField({ caption: e.target.value })}
                          data-testid={`real-img-caption-${i}`}
                        />
                      </div>
                    </div>
                    <div className="flex flex-col sm:flex-row gap-2 items-stretch sm:items-center">
                      <Input
                        placeholder="URL image (Cloudinary, https://..., ou base64)"
                        value={isB64 ? "" : (src || "")}
                        onChange={(e) => updateField({ src: e.target.value })}
                        className="flex-1 text-xs bg-cream"
                        data-testid={`real-img-url-${i}`}
                      />
                      <label className="inline-flex items-center justify-center gap-1.5 px-3 py-2 border border-sand cursor-pointer hover:bg-bone text-xs whitespace-nowrap">
                        <Plus className="w-3.5 h-3.5" /> Remplacer (upload)
                        <input type="file" accept="image/*" className="hidden" onChange={onReplaceUpload} />
                      </label>
                      <StoredImagePreviewButton src={src} label="Voir" />
                      <button
                        type="button"
                        onClick={() => removeGalleryImage(i)}
                        className="inline-flex items-center justify-center gap-1.5 px-3 py-2 text-corten text-xs hover:underline whitespace-nowrap"
                        aria-label="Supprimer cette image"
                      >
                        <Trash2 className="w-3.5 h-3.5" /> Supprimer
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
            <label className="inline-flex items-center gap-2 px-4 py-2 border border-sand cursor-pointer hover:bg-bone text-sm">
              <Plus className="w-4 h-4" /> Ajouter des images
              <input type="file" multiple accept="image/*" className="hidden" onChange={onAddImages} />
            </label>
          </div>

          <div>
            <Label className="label-eyebrow block mb-2">Vidéos ({(f.videos || []).length})</Label>
            <div className="space-y-2 mb-2">
              {(f.videos || []).map((v, i) => (
                <div key={i} className="flex items-center gap-2">
                  <video src={v} className="w-32 h-20 object-cover" />
                  <button type="button" onClick={() => removeVideo(i)} className="text-corten text-xs hover:underline">Supprimer</button>
                </div>
              ))}
            </div>
            <label className="inline-flex items-center gap-2 px-4 py-2 border border-sand cursor-pointer hover:bg-bone text-sm">
              <Plus className="w-4 h-4" /> Ajouter des vidéos
              <input type="file" multiple accept="video/*" className="hidden" onChange={onAddVideos} />
            </label>
          </div>
        </div>
        <DialogFooter className="sticky bottom-0 bg-cream pt-4 border-t border-sand -mx-6 -mb-6 px-6 pb-6">
          <Button variant="outline" onClick={handleClose} disabled={saving}>Fermer</Button>
          <Button
            onClick={submit}
            disabled={saving || !dirty}
            className="bg-forest hover:bg-forest-deep text-cream"
            data-testid="real-save"
          >
            <Save className="w-4 h-4 mr-2" /> {saving ? "Enregistrement..." : dirty ? "Enregistrer" : "✓ Enregistré"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------- MESSAGES ----------------
function MessagesPanel() {
  const [items, setItems] = useState([]);
  const load = () => api.get("/messages").then((r) => setItems(r.data || []));
  useEffect(() => { load(); }, []);

  const toggleRead = async (m) => {
    if (!m.read) await api.put(`/messages/${m.id}/read`); load();
  };
  const del = async (id) => {
    if (!confirm("Supprimer ce message ?")) return;
    await api.delete(`/messages/${id}`); toast.success("Supprimé"); load();
  };

  return (
    <div className="space-y-3">
      <h3 className="font-display text-2xl text-forest mb-4">Messages reçus ({items.length})</h3>
      {items.length === 0 && <p className="text-forest-soft">Aucun message pour le moment.</p>}
      {items.map((m) => (
        <details key={m.id} className="bg-cream border border-sand p-5" onToggle={(e) => e.target.open && toggleRead(m)}>
          <summary className="cursor-pointer flex items-center justify-between gap-4 list-none">
            <div className="flex items-center gap-3">
              {!m.read && <span className="w-2 h-2 bg-corten rounded-full" />}
              <div>
                <p className="text-forest font-medium">{m.prenom} {m.nom}</p>
                <p className="text-xs text-forest-soft">{new Date(m.created_at).toLocaleString("fr-CH")}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {m.type_demande && <Badge variant="outline">{m.type_demande}</Badge>}
              {m.read ? <EyeOff className="w-4 h-4 text-forest-soft" /> : <Eye className="w-4 h-4 text-corten" />}
            </div>
          </summary>
          <div className="mt-4 pt-4 border-t border-sand space-y-2 text-sm">
            <p><strong>Email:</strong> <a href={`mailto:${m.email}`} className="text-corten">{m.email}</a></p>
            <p><strong>Téléphone:</strong> <a href={`tel:${m.telephone}`} className="text-corten">{m.telephone}</a></p>
            {m.adresse_chantier && <p><strong>Chantier:</strong> {m.adresse_chantier}</p>}
            <p className="whitespace-pre-wrap mt-3">{m.message}</p>
            {m.photos?.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-3">
                {m.photos.map((p, i) => <img key={i} src={p} alt="" className="w-24 h-24 object-cover" />)}
              </div>
            )}
            <Button size="sm" variant="ghost" onClick={() => del(m.id)} className="text-corten mt-3">
              <Trash2 className="w-3.5 h-3.5 mr-1" /> Supprimer
            </Button>
          </div>
        </details>
      ))}
    </div>
  );
}

// ---------------- ACCOUNT ----------------
function AccountPanel() {
  const { user, refresh } = useAuth();
  const [curPwd, setCurPwd] = useState("");
  const [newPwd, setNewPwd] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const payload = { current_password: curPwd, new_password: newPwd || curPwd };
      if (newEmail) payload.new_email = newEmail;
      await api.post("/auth/change-password", payload);
      toast.success("Compte mis à jour");
      setCurPwd(""); setNewPwd(""); setNewEmail(""); refresh();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-6 max-w-2xl">
      <Card title="Compte administrateur">
        <p className="text-sm text-forest-soft">Connecté en tant que <strong>{user?.email}</strong>.</p>
        <form onSubmit={submit} className="space-y-3 mt-4">
          <div>
            <Label className="label-eyebrow block mb-2">Mot de passe actuel *</Label>
            <Input type="password" required value={curPwd} onChange={(e) => setCurPwd(e.target.value)} data-testid="current-password" />
          </div>
          <div>
            <Label className="label-eyebrow block mb-2">Nouveau mot de passe</Label>
            <Input type="password" value={newPwd} onChange={(e) => setNewPwd(e.target.value)} data-testid="new-password" />
          </div>
          <div>
            <Label className="label-eyebrow block mb-2">Nouvel email (optionnel)</Label>
            <Input type="email" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} placeholder={user?.email} data-testid="new-email" />
          </div>
          <Button type="submit" disabled={busy} className="bg-forest hover:bg-forest-deep text-cream" data-testid="save-account">
            {busy ? "Enregistrement..." : "Mettre à jour"}
          </Button>
        </form>
      </Card>

      <BackupPanel />
    </div>
  );
}

// ---------------- BACKUP / EXPORT / IMPORT ----------------
function BackupPanel() {
  const [downloading, setDownloading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [pendingFile, setPendingFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [mode, setMode] = useState("merge");
  const { confirm, dialog: confirmDialog } = useConfirm();

  const downloadBackup = async () => {
    setDownloading(true);
    try {
      const { data } = await api.get("/admin/export");
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const ts = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
      a.href = url;
      a.download = `pizzelli_backup_${ts}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      const counts = data.collections || {};
      toast.success(`✓ Sauvegarde téléchargée — ${counts.realizations?.length || 0} réalisation(s), ${counts.services?.length || 0} service(s), ${counts.messages?.length || 0} message(s)`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message || "Échec du téléchargement");
    } finally {
      setDownloading(false);
    }
  };

  const onPickFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.name.endsWith(".json")) {
      toast.error("Le fichier doit être un .json");
      return;
    }
    try {
      const text = await file.text();
      const json = JSON.parse(text);
      // Preview côté serveur (validation + résumé)
      const { data } = await api.post("/admin/import", { data: json, mode: "preview" });
      setPendingFile(json);
      setPreview(data.summary);
      setMode("merge");
    } catch (err) {
      toast.error(`Fichier invalide : ${err.message}`);
    }
  };

  const executeImport = async () => {
    if (!pendingFile || !preview) return;
    const description = mode === "replace"
      ? `Mode REMPLACEMENT TOTAL — toutes les données actuelles seront SUPPRIMÉES et remplacées :\n` +
        `• ${preview.counts.site_settings || 0} paramètres\n• ${preview.counts.services || 0} services\n` +
        `• ${preview.counts.realizations || 0} réalisations\n• ${preview.counts.gallery || 0} galerie\n` +
        `• ${preview.counts.messages || 0} messages\n\nUne sauvegarde automatique est créée AVANT l'import.\nLes comptes admin ne sont jamais touchés.`
      : `Mode FUSION — les éléments du fichier sont ajoutés ou mettent à jour les existants (par id).\n` +
        `Aucune donnée ne sera supprimée.\nUne sauvegarde automatique est créée avant l'import.`;
    const ok = await confirm({
      title: mode === "replace" ? "Confirmer le remplacement total" : "Confirmer la fusion",
      description,
      confirmLabel: mode === "replace" ? "Tout remplacer" : "Fusionner",
    });
    if (!ok) return;

    setImporting(true);
    try {
      const confirmToken = `CONFIRM-${preview.exported_at}`;
      const { data } = await api.post("/admin/import", {
        data: pendingFile,
        mode,
        confirm_token: confirmToken,
      });
      toast.success(`✓ Import effectué (${mode}) · sauvegarde auto: ${data.backup_id?.slice(0, 8)}…`);
      setPendingFile(null);
      setPreview(null);
      // Force un rechargement pour voir les nouvelles données
      setTimeout(() => window.location.reload(), 1500);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message || "Échec de l'import");
    } finally {
      setImporting(false);
    }
  };

  return (
    <Card title="Sauvegarde du site">
      {confirmDialog}
      <p className="text-sm text-forest-soft">
        Téléchargez une sauvegarde complète JSON (tous les contenus, images base64 incluses).
        Conservez ce fichier en lieu sûr — il vous permettra de restaurer le site en cas de besoin.
      </p>

      <div className="space-y-3 mt-4">
        <Button onClick={downloadBackup} disabled={downloading} className="bg-forest hover:bg-forest-deep text-cream" data-testid="download-backup">
          <Save className="w-4 h-4 mr-2" /> {downloading ? "Préparation..." : "Télécharger une sauvegarde complète"}
        </Button>

        <div className="pt-4 border-t border-sand">
          <p className="label-eyebrow mb-2">Restaurer depuis un fichier</p>
          <p className="text-xs text-forest-soft mb-3">
            ⚠ Sélectionnez un fichier <code>.json</code> exporté précédemment. Vous verrez d'abord un récapitulatif avant toute action. Une sauvegarde automatique est faite avant tout import.
          </p>
          <label className="inline-flex items-center gap-2 px-4 py-2 border border-sand cursor-pointer hover:bg-bone text-sm" data-testid="import-pick">
            <Plus className="w-4 h-4" /> Choisir un fichier de sauvegarde
            <input type="file" accept=".json,application/json" className="hidden" onChange={onPickFile} />
          </label>
        </div>
      </div>

      {preview && (
        <div className="mt-6 border border-corten/30 bg-corten/5 p-4 rounded space-y-3">
          <div className="flex items-center gap-2 text-corten">
            <AlertCircle className="w-4 h-4" /> <strong>Récapitulatif du fichier — aucune action effectuée</strong>
          </div>
          <ul className="text-sm text-forest space-y-1">
            <li>• App : <strong>{preview.app}</strong> (version {preview.version})</li>
            <li>• Exporté le : <strong>{preview.exported_at && new Date(preview.exported_at).toLocaleString("fr-CH")}</strong></li>
            <li>• Site settings : <strong>{preview.counts.site_settings || 0}</strong></li>
            <li>• Services : <strong>{preview.counts.services || 0}</strong></li>
            <li>• Réalisations : <strong>{preview.counts.realizations || 0}</strong></li>
            <li>• Galerie : <strong>{preview.counts.gallery || 0}</strong></li>
            <li>• Messages : <strong>{preview.counts.messages || 0}</strong></li>
            <li>• Admins (référence seulement, NON modifiés) : <strong>{preview.users_summary?.length || 0}</strong></li>
          </ul>

          <div className="flex flex-col gap-2 pt-3 border-t border-corten/20">
            <p className="text-xs font-medium text-forest">Choisir le mode :</p>
            <label className="flex items-start gap-2 cursor-pointer text-sm">
              <input type="radio" name="import-mode" value="merge" checked={mode === "merge"} onChange={() => setMode("merge")} className="mt-1" />
              <span><strong>Fusion (recommandé)</strong> — ajoute ou met à jour, ne supprime rien</span>
            </label>
            <label className="flex items-start gap-2 cursor-pointer text-sm">
              <input type="radio" name="import-mode" value="replace" checked={mode === "replace"} onChange={() => setMode("replace")} className="mt-1" />
              <span className="text-corten"><strong>Remplacement total</strong> — supprime tout et remet uniquement le contenu du fichier (sauf comptes admin)</span>
            </label>
          </div>

          <div className="flex gap-2 pt-2">
            <Button variant="outline" onClick={() => { setPreview(null); setPendingFile(null); }} disabled={importing}>
              Annuler
            </Button>
            <Button onClick={executeImport} disabled={importing || !preview.valid} className={mode === "replace" ? "bg-corten hover:bg-corten-dark text-cream" : "bg-forest hover:bg-forest-deep text-cream"}>
              {importing ? "Import en cours..." : (mode === "replace" ? "Tout remplacer" : "Fusionner")}
            </Button>
          </div>
          {!preview.valid && <p className="text-xs text-corten">Ce fichier ne semble pas être un export Pizzelli valide.</p>}
        </div>
      )}
    </Card>
  );
}
